import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Calendar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { getPostBySlug, formatDate } from "@/data/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    document.title = post.metaTitle;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", post.metaDescription);
    window.scrollTo({ top: 0 });
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex flex-col items-center justify-center gap-4 px-6">
        <p className="font-head font-bold text-2xl text-slate-100">Post not found</p>
        <Button
          data-testid="blog-post-notfound-back"
          onClick={() => navigate("/blog")}
          className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
        >
          Back to blog
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="blog-post-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 h-16 border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto h-full px-6 flex items-center gap-3">
          <img
            src="/pitcrew-logo-horizontal.png"
            alt="Pitcrew"
            data-testid="blog-post-logo"
            className="h-9 md:h-11 w-auto cursor-pointer"
            onClick={() => navigate("/")}
          />
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            <Button
              variant="ghost"
              size="sm"
              data-testid="blog-post-nav-blog"
              onClick={() => navigate("/blog")}
              className="px-2.5 sm:px-3 text-slate-300 hover:text-white hover:bg-slate-800"
            >
              Blog
            </Button>
            <Button
              data-testid="blog-post-nav-diagnosis"
              onClick={() => navigate("/")}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
            >
              {user ? "Go to Dashboard" : "Get Instant AI Diagnosis"}
            </Button>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <button
          data-testid="blog-post-back"
          onClick={() => navigate("/blog")}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-amber-400 text-sm mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> All articles
        </button>

        <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
          <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-semibold">
            {post.category}
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {formatDate(post.date)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {post.readTime}
          </span>
        </div>

        <h1 className="font-head font-black text-3xl sm:text-4xl text-slate-50 leading-tight">
          {post.title}
        </h1>

        <div className="mt-8 rounded-2xl overflow-hidden border border-slate-800">
          <img
            src={post.image}
            alt={post.title}
            data-testid="blog-post-featured-image"
            className="w-full object-cover"
          />
        </div>

        {/* Body */}
        <div className="blog-content mt-10">{post.content}</div>

        {/* CTA */}
        <div
          data-testid="blog-post-cta"
          className="mt-14 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-slate-900/40 p-8 text-center"
        >
          <h3 className="font-head font-black text-2xl text-slate-50">
            Get your free AI car diagnosis
          </h3>
          <p className="text-slate-400 mt-2 max-w-md mx-auto">
            Describe your symptoms and get an instant, expert-grade report — then compare bids from
            local mechanics. No obligation.
          </p>
          <Button
            data-testid="blog-post-cta-button"
            onClick={() => navigate("/")}
            className="mt-6 h-12 px-7 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-base"
          >
            Get Instant AI Diagnosis <ArrowRight className="h-5 w-5 ml-1.5" />
          </Button>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center gap-3 justify-between text-sm text-slate-500">
          <img src="/pitcrew-logo-horizontal.png" alt="Pitcrew" className="h-7 object-contain" />
          <p>© {new Date().getFullYear()} · Professional auto repair & service</p>
          <button
            data-testid="blog-post-footer-home"
            onClick={() => navigate("/")}
            className="hover:text-slate-300"
          >
            Back to home
          </button>
        </div>
      </footer>
    </div>
  );
}
